// v1.9 P0: 错误状态组件 — 4 种 (NotFound / Server / Forbidden / Network)
// 替代路由层零散 "div 错误" 的写法
//
// 用法:
//   <ErrorState kind="not-found" />
//   <ErrorState kind="server" error={err} onRetry={fn} />
//   <ErrorState kind="forbidden" />
//   <ErrorState kind="network" onRetry={fn} />
import { Link, useRouter } from '@tanstack/react-router';
import { Icon } from './Icon';
import { Button } from './ui/Button';
import { Card } from './ui/Card';

export type ErrorStateKind = 'not-found' | 'server' | 'forbidden' | 'network' | 'generic';

interface ErrorStateProps {
  kind?: ErrorStateKind;
  /** 自定义标题 (会覆盖默认) */
  title?: string;
  /** 自定义描述 */
  description?: string;
  /** 错误对象 (用于 server 错误显示) */
  error?: Error | null;
  /** 重试回调 (server/network 错误显示) */
  onRetry?: () => void;
  /** 自定义 className */
  className?: string;
  /** 自定义底部链接 (如 ← 返回) */
  footerLink?: { to: string; label: string };
}

const META: Record<
  ErrorStateKind,
  {
    icon: string;
    iconCls: string;
    defaultTitle: string;
    defaultDescription: string;
    status: string;
  }
> = {
  'not-found': {
    icon: 'search-x',
    iconCls: 'text-ink-3 bg-surface-2',
    defaultTitle: '页面不存在',
    defaultDescription: '链接可能已失效, 或地址输入有误',
    status: '404',
  },
  server: {
    icon: 'server-crash',
    iconCls: 'text-bad bg-bad/10',
    defaultTitle: '服务器出错了',
    defaultDescription: '我们正在抢修, 请稍后重试',
    status: '500',
  },
  forbidden: {
    icon: 'lock',
    iconCls: 'text-warn bg-warn/10',
    defaultTitle: '没有访问权限',
    defaultDescription: '请联系管理员申请权限',
    status: '403',
  },
  network: {
    icon: 'wifi-off',
    iconCls: 'text-info bg-info/10',
    defaultTitle: '网络连接异常',
    defaultDescription: '检查网络后重试, 或刷新页面',
    status: 'NET',
  },
  generic: {
    icon: 'alert-circle',
    iconCls: 'text-bad bg-bad/10',
    defaultTitle: '出错了',
    defaultDescription: '请重试或返回首页',
    status: 'ERR',
  },
};

export function ErrorState({
  kind = 'generic',
  title,
  description,
  error,
  onRetry,
  className,
  footerLink,
}: ErrorStateProps) {
  const router = useRouter();
  const meta = META[kind];

  function handleGoBack() {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    } else {
      router.navigate({ to: '/' });
    }
  }

  function handleReport() {
    if (typeof window === 'undefined') return;
    const subject = encodeURIComponent(`[Trade OS] 错误报告: ${meta.defaultTitle}`);
    const body = encodeURIComponent(
      `错误类型: ${meta.status} ${kind}\n时间: ${new Date().toISOString()}\nURL: ${window.location.href}\n${error ? `\n错误信息:\n${error.message}\n\n${error.stack ?? ''}` : ''}`,
    );
    window.open(`mailto:support@trade-os.local?subject=${subject}&body=${body}`);
  }

  return (
    <div className={`min-h-[60vh] flex items-center justify-center p-4 sm:p-6 ${className ?? ''}`}>
      <Card padding="lg" className="w-full max-w-md text-center animate-fade-up">
        <div
          className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${meta.iconCls}`}
        >
          <Icon name={meta.icon} size="xl" aria-hidden />
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-3 mt-3">
          {meta.status}
        </div>

        <h1 className="text-lg sm:text-xl font-semibold text-ink mt-1">
          {title ?? meta.defaultTitle}
        </h1>
        <p className="text-sm text-ink-3 mt-2 leading-relaxed">
          {description ?? meta.defaultDescription}
        </p>

        {error && kind === 'server' && (
          <details className="mt-4 text-left text-xs">
            <summary className="cursor-pointer text-ink-3 hover:text-ink-2 select-none">
              查看错误详情
            </summary>
            <pre className="mt-2 p-2 bg-surface-2 rounded overflow-auto max-h-40 whitespace-pre-wrap leading-relaxed text-ink-2">
              {error.message}
              {error.stack ? `\n\n${error.stack.split('\n').slice(0, 4).join('\n')}` : ''}
            </pre>
          </details>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          {onRetry && (
            <Button variant="primary" size="md" onClick={onRetry} iconLeft="refresh-cw">
              重试
            </Button>
          )}
          <Button variant="secondary" size="md" onClick={handleGoBack} iconLeft="arrow-left">
            返回上一页
          </Button>
          <Button
            variant="ghost"
            size="md"
            onClick={() => router.navigate({ to: '/' })}
            iconLeft="home"
          >
            回首页
          </Button>
        </div>

        {footerLink && (
          <div className="mt-4">
            <Link to={footerLink.to} className="text-xs text-ink-3 hover:text-accent">
              {footerLink.label}
            </Link>
          </div>
        )}

        {kind === 'server' && (
          <div className="mt-4 pt-4 border-t border-line">
            <button
              type="button"
              onClick={handleReport}
              className="text-xs text-ink-3 hover:text-accent transition-colors inline-flex items-center gap-1"
            >
              <Icon name="mail" size="xs" strokeWidth={2} />
              报告问题
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}
